const FONT_FAMILY = 'Arial, sans-serif';
const FONT_SIZE = 14;
const FONT = `${FONT_SIZE}px ${FONT_FAMILY}`;
const LINE_HEIGHT = 20;
const CELL_PADDING_X = 12;
const CELL_PADDING_Y = 10;
const GRID_COLOR = '#cfd8e3';
const TEXT_COLOR = '#1f2937';
const BACKGROUND_COLOR = '#ffffff';
const MIN_CELL_WIDTH = 96;
const MAX_CELL_WIDTH = 320;
const MAX_CANVAS_WIDTH = 1800;
const BORDER_WIDTH = 1;

function normalizeCellText(value) {
    return String(value ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/\u00a0/g, ' ')
        .trim();
}

function trimEmptyRows(rows) {
    const normalizedRows = [...rows];
    while (normalizedRows.length > 0) {
        const lastRow = normalizedRows[normalizedRows.length - 1] || [];
        const hasValue = lastRow.some((cell) => normalizeCellText(cell) !== '');
        if (hasValue) {
            break;
        }
        normalizedRows.pop();
    }

    return normalizedRows;
}

function trimEmptyColumns(rows) {
    if (rows.length === 0) {
        return rows;
    }

    let maxColumns = 0;
    rows.forEach((row) => {
        maxColumns = Math.max(maxColumns, Array.isArray(row) ? row.length : 0);
    });

    let trailingEmptyColumns = 0;
    for (let columnIndex = maxColumns - 1; columnIndex >= 0; columnIndex -= 1) {
        const hasValue = rows.some((row) => normalizeCellText(row?.[columnIndex]) !== '');
        if (hasValue) {
            break;
        }
        trailingEmptyColumns += 1;
    }

    if (trailingEmptyColumns === 0) {
        return rows;
    }

    return rows.map((row) => row.slice(0, Math.max(0, row.length - trailingEmptyColumns)));
}

function extractRowsFromHtml(html) {
    const normalizedHtml = String(html || '').trim();
    if (!normalizedHtml) {
        return [];
    }

    try {
        const parser = new DOMParser();
        const documentNode = parser.parseFromString(normalizedHtml, 'text/html');
        const table = documentNode.querySelector('table');
        if (!table) {
            return [];
        }

        const rows = Array.from(table.querySelectorAll('tr')).map((row) =>
            Array.from(row.querySelectorAll('th, td')).map((cell) => normalizeCellText(cell.textContent))
        );

        return trimEmptyColumns(trimEmptyRows(rows.filter((row) => row.length > 0)));
    } catch {
        return [];
    }
}

function extractRowsFromPlainText(text) {
    const normalizedText = String(text || '').replace(/\r\n?/g, '\n');
    if (!normalizedText.trim()) {
        return [];
    }

    const rows = normalizedText
        .split('\n')
        .map((row) => row.split('\t').map((cell) => normalizeCellText(cell)));

    return trimEmptyColumns(trimEmptyRows(rows));
}

function buildGridRows({ html, text }) {
    const htmlRows = extractRowsFromHtml(html);
    if (htmlRows.length > 0) {
        return htmlRows;
    }

    return extractRowsFromPlainText(text);
}

function splitLongWord(context, word, maxWidth) {
    const segments = [];
    let current = '';

    for (const character of word) {
        const candidate = current + character;
        if (current && context.measureText(candidate).width > maxWidth) {
            segments.push(current);
            current = character;
        } else {
            current = candidate;
        }
    }

    if (current) {
        segments.push(current);
    }

    return segments.length > 0 ? segments : [''];
}

function wrapParagraph(context, paragraph, maxWidth) {
    const normalizedParagraph = normalizeCellText(paragraph);
    if (normalizedParagraph === '') {
        return [''];
    }

    const words = normalizedParagraph.split(/\s+/);
    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (context.measureText(candidate).width <= maxWidth) {
            currentLine = candidate;
            return;
        }

        if (currentLine) {
            lines.push(currentLine);
            currentLine = '';
        }

        if (context.measureText(word).width <= maxWidth) {
            currentLine = word;
            return;
        }

        const segments = splitLongWord(context, word, maxWidth);
        segments.forEach((segment, index) => {
            if (index === segments.length - 1) {
                currentLine = segment;
            } else {
                lines.push(segment);
            }
        });
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [''];
}

function wrapCellText(context, text, maxWidth) {
    const paragraphs = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
    const wrappedLines = [];

    paragraphs.forEach((paragraph, index) => {
        const lines = wrapParagraph(context, paragraph, maxWidth);
        wrappedLines.push(...lines);
        if (index < paragraphs.length - 1 && lines[lines.length - 1] !== '') {
            wrappedLines.push('');
        }
    });

    return wrappedLines.length > 0 ? wrappedLines : [''];
}

function measurePreferredWidth(context, text) {
    const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
    let maxLineWidth = 0;

    lines.forEach((line) => {
        const width = context.measureText(normalizeCellText(line)).width;
        maxLineWidth = Math.max(maxLineWidth, width);
    });

    return Math.min(
        MAX_CELL_WIDTH,
        Math.max(MIN_CELL_WIDTH, Math.ceil(maxLineWidth + CELL_PADDING_X * 2))
    );
}

function fitColumnWidths(widths) {
    const contentLimit = MAX_CANVAS_WIDTH - BORDER_WIDTH * (widths.length + 1);
    const currentTotal = widths.reduce((sum, width) => sum + width, 0);
    if (currentTotal <= contentLimit || contentLimit <= 0) {
        return widths;
    }

    const scale = contentLimit / currentTotal;
    return widths.map((width) => Math.max(MIN_CELL_WIDTH, Math.floor(width * scale)));
}

function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
                return;
            }
            reject(new Error('Не удалось подготовить изображение таблицы.'));
        }, 'image/png');
    });
}

export async function clipboardPayloadToTableImageFile({ html = '', text = '' } = {}) {
    const rows = buildGridRows({ html, text });
    if (rows.length === 0) {
        return null;
    }

    const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
    if (columnCount === 0) {
        return null;
    }

    const normalizedRows = rows.map((row) =>
        Array.from({ length: columnCount }, (_, index) => normalizeCellText(row[index] || ''))
    );

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Не удалось создать canvas для таблицы.');
    }

    context.font = FONT;

    const columnWidths = fitColumnWidths(
        Array.from({ length: columnCount }, (_, columnIndex) =>
            normalizedRows.reduce(
                (maxWidth, row) => Math.max(maxWidth, measurePreferredWidth(context, row[columnIndex])),
                MIN_CELL_WIDTH
            )
        )
    );

    const wrappedRows = normalizedRows.map((row) =>
        row.map((cell, columnIndex) =>
            wrapCellText(context, cell, Math.max(1, columnWidths[columnIndex] - CELL_PADDING_X * 2))
        )
    );

    const rowHeights = wrappedRows.map((row) => {
        const lineCount = row.reduce((maxLines, cellLines) => Math.max(maxLines, cellLines.length), 1);
        return lineCount * LINE_HEIGHT + CELL_PADDING_Y * 2;
    });

    const totalWidth = columnWidths.reduce((sum, width) => sum + width, BORDER_WIDTH * (columnWidths.length + 1));
    const totalHeight = rowHeights.reduce((sum, height) => sum + height, BORDER_WIDTH * (rowHeights.length + 1));

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    context.fillStyle = BACKGROUND_COLOR;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.font = FONT;
    context.textBaseline = 'top';
    context.strokeStyle = GRID_COLOR;
    context.fillStyle = TEXT_COLOR;
    context.lineWidth = BORDER_WIDTH;

    let y = BORDER_WIDTH;
    wrappedRows.forEach((row, rowIndex) => {
        let x = BORDER_WIDTH;
        row.forEach((cellLines, columnIndex) => {
            const cellWidth = columnWidths[columnIndex];
            const cellHeight = rowHeights[rowIndex];

            context.fillStyle = BACKGROUND_COLOR;
            context.fillRect(x, y, cellWidth, cellHeight);
            context.strokeStyle = GRID_COLOR;
            context.strokeRect(x, y, cellWidth, cellHeight);

            context.fillStyle = TEXT_COLOR;
            cellLines.forEach((line, lineIndex) => {
                context.fillText(
                    line,
                    x + CELL_PADDING_X,
                    y + CELL_PADDING_Y + lineIndex * LINE_HEIGHT
                );
            });

            x += cellWidth + BORDER_WIDTH;
        });

        y += rowHeights[rowIndex] + BORDER_WIDTH;
    });

    const blob = await canvasToBlob(canvas);
    const fileName = `google-sheets-${Date.now()}.png`;

    return new File([blob], fileName, { type: 'image/png' });
}
